import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token. Please use the link from your email.');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success('Password updated. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-light tracking-tight mb-2" style={{ fontFamily: 'Spectral, serif' }}>
              Stable Stationary
            </h1>
          </Link>
          <p className="text-sm text-[#737373]">Choose a new password</p>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          {!token ? (
            <div className="text-center space-y-4" data-testid="reset-invalid-view">
              <p className="text-[#1A1A1A]" style={{ fontFamily: 'Spectral, serif', fontSize: '18px' }}>
                This link is missing a token.
              </p>
              <p className="text-sm text-[#737373]">Request a fresh reset link to continue.</p>
              <Link to="/forgot-password" className="text-[#B94A3E] hover:underline text-sm" data-testid="reset-request-new-link">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="reset-form">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password" className="label-text">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm" className="label-text">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2 border-[#E5E0D8] focus:ring-[#B94A3E]"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
              </div>
              <div className="mt-6 text-center text-sm">
                <Link to="/login" className="text-[#B94A3E] hover:underline" data-testid="reset-login-link">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
